import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { clearReturnTo } from "../lib/returnTo";
import { AUTH_REQUEST_TIMEOUT_MS, authRequestErrorMessage, configureAxiosDefaults } from "./authRequest";
import { authDiagLog } from "./authDiag";

const ENV_BACKEND = process.env.REACT_APP_BACKEND_URL;

// Runtime backend resolver:
//   - Use the current page origin when running in the browser (so zcars.ai calls
//     zcars.ai/api/... — same-origin, no third-party cookies, works in Safari/
//     Firefox/Brave/iOS-webviews).
//   - Fall back to the build-time env var only for localhost dev (where /api is
//     not proxied to the backend port on the dev server) or SSR.
const LOCAL_BACKEND_DEFAULT = "http://localhost:8010";

function resolveBackendUrl() {
  // CRA inlines env at build time; unset REACT_APP_BACKEND_URL becomes literal "undefined" if misconfigured.
  const envBackend =
    ENV_BACKEND &&
    ENV_BACKEND !== "undefined" &&
    !String(ENV_BACKEND).startsWith("undefined")
      ? ENV_BACKEND
      : null;
  if (typeof window === "undefined") return envBackend ?? LOCAL_BACKEND_DEFAULT;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") {
    return envBackend ?? LOCAL_BACKEND_DEFAULT;
  }
  return window.location.origin;
}

const BACKEND_URL = resolveBackendUrl();
export { BACKEND_URL, AUTH_REQUEST_TIMEOUT_MS };
export const API = `${BACKEND_URL}/api`;

configureAxiosDefaults();

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const authDiagPrevRef = useRef({ loading, authError });

  useEffect(() => {
    const prev = authDiagPrevRef.current;
    if (prev.loading !== loading || prev.authError !== authError) {
      authDiagLog("AuthContext", {
        loading,
        authError,
        prevLoading: prev.loading,
        prevAuthError: prev.authError,
      });
      authDiagPrevRef.current = { loading, authError };
    }
  }, [loading, authError]);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        timeout: AUTH_REQUEST_TIMEOUT_MS,
      });
      setUser(res.data);
      setAuthError(null);
    } catch (e) {
      setUser(null);
      if (e?.code === "ECONNABORTED" || !e?.response) {
        setAuthError(authRequestErrorMessage(e));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const retryAuth = useCallback(() => {
    setAuthError(null);
    setLoading(true);
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    // Legacy Emergent OAuth used #session_id= in the hash; strip it so GIS login proceeds.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    clearReturnTo();
    setUser(null);
    window.location.href = "/";
  };

  const updateRole = async (role) => {
    const res = await axios.patch(`${API}/auth/role`, { role }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, authError, setUser, checkAuth, retryAuth, logout, updateRole }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
